import React, { useEffect, useState } from "react";
import { Download, Share, MoreVertical } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * "Download App" button — installs Hifz with Hikmah as a PWA.
 *
 * Chrome/Edge/Android fire `beforeinstallprompt`; we stash that event and
 * replay it via `.prompt()` on click, which is the only way a page can
 * trigger the native install UI (there is no direct "install now" API).
 * iOS Safari never fires that event — there is no programmatic install
 * there at all — so on iOS (and any other browser that hasn't offered a
 * native prompt) the click instead opens a short manual walkthrough
 * ("Add to Home Screen" for iOS, "look for the install icon" elsewhere).
 * Requires `public/manifest.json` + `public/sw.js` (registered in
 * `src/index.js`) for the browser to consider the site installable at all.
 */
function detectIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function InstallAppButton({ className }) {
  const { t } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const isIos = detectIos();

  useEffect(() => {
    const standaloneQuery = window.matchMedia("(display-mode: standalone)");
    setIsStandalone(standaloneQuery.matches || window.navigator.standalone === true);

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Already running as an installed app — nothing to offer.
  if (isStandalone) return null;

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {
        // ignore — user dismissed or the browser cancelled the prompt
      }
      setDeferredPrompt(null);
      return;
    }
    setShowHelp(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={className || "btn-ghost text-sm"}
        data-testid="nav-install-app"
      >
        <Download className="h-4 w-4" /> {t("installAppButton.downloadApp")}
      </button>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle>{t("installAppButton.installHifzWithHikmah")}</DialogTitle>
            <DialogDescription>
              {isIos
                ? t("installAppButton.addThisAppToYour")
                : t("installAppButton.addThisAppToYour2")}
            </DialogDescription>
          </DialogHeader>
          {isIos ? (
            <ol className="text-sm text-foreground/80 space-y-2.5 list-decimal list-inside">
              <li>
                {t("installAppButton.tapThe")} <Share className="inline h-4 w-4 -mt-0.5" aria-hidden="true" /> {t("installAppButton.shareIconInSafariS")}
              </li>
              <li>
                {t("installAppButton.scrollDownAndTap")} <span className="font-semibold text-forest-700">{t("installAppButton.addToHomeScreen")}</span>.
              </li>
              <li>
                {t("installAppButton.tap")} <span className="font-semibold text-forest-700">{t("installAppButton.add")}</span> {t("installAppButton.toConfirm")}
              </li>
            </ol>
          ) : (
            <div className="text-sm text-foreground/80 space-y-3">
              <p>{t("installAppButton.lookForAnInstallIcon")}</p>
              <p>
                {t("installAppButton.donTSeeOneOpen")}
                <MoreVertical className="inline h-4 w-4 -mt-0.5" aria-hidden="true" />{t("installAppButton.andLookFor")}
                <span className="font-semibold text-forest-700">{t("installAppButton.installApp")}</span> {t("installAppButton.or")}
                <span className="font-semibold text-forest-700">{t("installAppButton.addToHomeScreen2")}</span>.
              </p>
              <p className="text-muted-foreground">{t("installAppButton.worksBestInChromeEdge")}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
